import { useEffect, useRef, useState } from 'react';
import { api, clock, humanise, km, type TripGeometry } from './api';

/**
 * Loads the Maps JavaScript API once per page.
 *
 * Google's loader is a global side effect — calling it twice throws — so the
 * promise is memoised at module scope rather than per component.
 */
let loader: Promise<void> | null = null;

export function loadMaps(key: string): Promise<void> {
  if (loader) return loader;
  loader = new Promise((resolve, reject) => {
    if ((window as { google?: unknown }).google) return resolve();
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=geometry&v=weekly`;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Could not load Google Maps'));
    document.head.appendChild(s);
  });
  return loader;
}

/**
 * Decodes an encoded polyline.
 *
 * Done here rather than with google.maps.geometry so the planned line can be
 * drawn the instant the data arrives, without waiting on the script — and so
 * this component is not silently coupled to a library that may fail to load.
 * Same algorithm as apps/api/src/geo/geo.math.ts.
 */
export function decodePolyline(str: string, precision = 5): { lat: number; lng: number }[] {
  const factor = 10 ** precision;
  const out: { lat: number; lng: number }[] = [];
  let index = 0, lat = 0, lng = 0;
  while (index < str.length) {
    let shift = 0, result = 0, byte: number;
    do {
      byte = str.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    lat += result & 1 ? ~(result >> 1) : result >> 1;
    shift = 0; result = 0;
    do {
      byte = str.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    lng += result & 1 ? ~(result >> 1) : result >> 1;
    out.push({ lat: lat / factor, lng: lng / factor });
  }
  return out;
}

export function TripMap({ tripId, onClose }: { tripId: string; onClose: () => void }) {
  const el = useRef<HTMLDivElement>(null);
  const [trip, setTrip] = useState<TripGeometry | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [needsKey, setNeedsKey] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [geo, cfg] = await Promise.all([api.tripGeometry(tripId), api.config()]);
        if (cancelled) return;
        setTrip(geo);
        if (!cfg.mapsBrowserKey) {
          setNeedsKey(true);
          return;
        }
        await loadMaps(cfg.mapsBrowserKey);
        if (cancelled || !el.current) return;
        draw(el.current, geo);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'Could not load the route');
      }
    })();
    return () => { cancelled = true; };
  }, [tripId]);

  return (
    <div className="mapwrap">
      <div className="map-head">
        <strong>
          {trip ? `${trip.driver_name ?? 'Driver'} · ${humanise(trip.status)}` : 'Loading…'}
        </strong>
        {trip ? (
          <span className="map-meta num">
            planned {km(trip.planned_distance_m)}
            {trip.actual_distance_m ? ` · actual ${km(trip.actual_distance_m)}` : ''}
            {trip.started_at ? ` · ${clock(trip.started_at)}` : ''}
            {trip.completed_at ? `–${clock(trip.completed_at)}` : ''}
          </span>
        ) : null}
        <div className="spacer" />
        <button className="btn sm" onClick={onClose}>Close</button>
      </div>

      {err ? <div className="empty">{err}</div> : null}

      {needsKey ? (
        <div className="empty" style={{ textAlign: 'left', padding: '18px 16px' }}>
          <strong>No map key configured.</strong>
          <p style={{ margin: '6px 0 0', maxWidth: '72ch' }}>
            Set <code>GOOGLE_MAPS_BROWSER_KEY</code> in <code>.env</code> and restart the
            API. It needs the <em>Maps JavaScript API</em> enabled and an HTTP-referrer
            restriction — a third key, separate from the server and mobile ones, because
            the three need different restrictions.
          </p>
          <p style={{ margin: '8px 0 0', maxWidth: '72ch' }}>
            The route has to render on a Google map: the planned line comes from the
            Routes API, and Google&rsquo;s terms do not allow their geometry to be drawn
            on someone else&rsquo;s basemap.
          </p>
        </div>
      ) : (
        <>
          <div ref={el} className="map" />
          <div className="map-key">
            <span><i className="planned" /> planned by the matcher</span>
            <span><i className="actual" /> actually driven (GPS)</span>
            {trip && trip.actualPath.length === 0 ? (
              <span style={{ color: 'var(--ink-faint)' }}>
                no GPS recorded — the driver app writes breadcrumbs only once a trip
                is started, and flushes them on completion
              </span>
            ) : null}
          </div>
        </>
      )}

      {trip ? (
        <table className="legs">
          <thead>
            <tr>
              <th>#</th><th>Rider</th><th>Pickup</th><th>Status</th>
              <th className="num">Picked up</th><th className="num">Dropped</th>
            </tr>
          </thead>
          <tbody>
            {trip.legs.map((l) => (
              <tr key={l.id}>
                <td className="num shrink">{l.seq}</td>
                <td>{l.riderName ?? 'Rider'}</td>
                <td>{l.pickup.address ?? '—'}</td>
                <td className="shrink">{humanise(l.status)}</td>
                <td className="num shrink">{clock(l.pickedUpAt)}</td>
                <td className="num shrink">{clock(l.droppedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Drawn with the raw Maps API rather than a React wrapper.
 *
 * One map, drawn once, never re-rendered — a wrapper library would be more
 * code and another dependency to keep current for no benefit here.
 */
function draw(host: HTMLDivElement, trip: TripGeometry) {
  const g = (window as any).google;
  const map = new g.maps.Map(host, {
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    clickableIcons: false,
  });
  const bounds = new g.maps.LatLngBounds();

  // Planned: a solid line, the route the matcher committed to.
  const planned = trip.planned_polyline ? decodePolyline(trip.planned_polyline) : [];
  if (planned.length > 1) {
    new g.maps.Polyline({
      map, path: planned, strokeColor: '#3b5bdb', strokeOpacity: 0.85, strokeWeight: 5,
    });
    planned.forEach((p) => bounds.extend(p));
  }

  // Actual: dashed over the top, so where the two diverge is the whole story.
  const actual = trip.actualPath.map((p) => ({ lat: p.lat, lng: p.lng }));
  if (actual.length > 1) {
    new g.maps.Polyline({
      map,
      path: actual,
      strokeOpacity: 0,
      icons: [{
        icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, strokeColor: '#c0392b', scale: 3 },
        offset: '0',
        repeat: '12px',
      }],
    });
    actual.forEach((p) => bounds.extend(p));
  }

  trip.legs.forEach((l) => {
    new g.maps.Marker({
      map,
      position: l.pickup,
      label: { text: String(l.seq), color: '#fff', fontSize: '11px', fontWeight: '700' },
      title: `${l.riderName ?? 'Rider'} — pickup\n${l.pickup.address ?? ''}`,
      icon: {
        path: g.maps.SymbolPath.CIRCLE,
        scale: 9,
        fillColor: l.status === 'dropped' ? '#0f8a5f' : '#a8630a',
        fillOpacity: 1,
        strokeColor: '#fff',
        strokeWeight: 2,
      },
    });
    bounds.extend(l.pickup);
    bounds.extend(l.drop);
  });

  if (!bounds.isEmpty()) {
    map.fitBounds(bounds, 48);
  } else {
    // No geometry at all — centre on Noida rather than the Atlantic.
    map.setCenter({ lat: 28.57, lng: 77.36 });
    map.setZoom(11);
  }
}
